import { useContext, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CartContext } from './CardContext';

const parseUserParam = (rawUser) => {
  const decoders = [
    (value) => value,
    (value) => decodeURIComponent(value),
    (value) => atob(value),
  ];

  for (const decode of decoders) {
    try {
      const decoded = decode(rawUser);
      const parsed = JSON.parse(decoded);
      if (parsed && typeof parsed === 'object') return parsed;
    } catch (error) {
      // ignore and try the next strategy
    }
  }

  return null;
};

const normalizeRedirect = (redirectParam) => {
  if (!redirectParam) return '/home';

  try {
    const decoded = decodeURIComponent(redirectParam).trim();

    if (/^https?:\/\//i.test(decoded)) {
      const url = new URL(decoded);
      return `${url.pathname}${url.search}${url.hash}` || '/home';
    }

    return decoded.startsWith('/') ? decoded : `/${decoded}`;
  } catch (error) {
    return '/home';
  }
};

const normalizeUser = (payload) => {
  const userPayload = payload.user ?? payload;

  return {
    affiliation: userPayload.affiliation ?? null,
    email: userPayload.email ?? null,
    name: userPayload.name ?? userPayload.given_name ?? '',
    country_id: userPayload.country_id ?? null,
    lastname: userPayload.lastname ?? userPayload.family_name ?? '',
    phone: userPayload.phone ?? null,
    code_phone: userPayload.code_phone ?? null,
    date_birth: userPayload.date_birth ?? null,
  };
};

export const UrlAuthProvider = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { loadCartFromLocalStorage } = useContext(CartContext);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const rawUser = params.get('user');
    const redirectParam = params.get('redirect');
    const cartId = params.get('cartId') ?? '';

    if (!rawUser && !redirectParam) return;

    if (rawUser) {
      const parsedPayload = parseUserParam(rawUser);

      if (parsedPayload) {
        const normalizedUser = normalizeUser(parsedPayload);
        const token = parsedPayload.api_token ?? parsedPayload.token ?? parsedPayload.token_arteonu;
        const gg = parsedPayload.gg ?? parsedPayload.google_api_key ?? parsedPayload.api_key;

        localStorage.setItem('user_arteonu', JSON.stringify(normalizedUser));
        if (token) {
          localStorage.setItem('token_arteonu', token);
        }
        if (gg) {
          localStorage.setItem('gg', gg);
        }

        localStorage.setItem('cartId', cartId);

        loadCartFromLocalStorage();
      }
    }

    if (redirectParam) {
      navigate(normalizeRedirect(redirectParam), { replace: true });
    } else if (location.search) {
      navigate(location.pathname, { replace: true });
    }
  }, [location.pathname, location.search, navigate, loadCartFromLocalStorage]);

  return <>{children}</>;
};
